import type {
    ExamPerformanceTypes, PracticePerformance, StagePerformance, StudyUnitPerformance,
} from '@/types/Performance';
import { type Question, QuestionStatus, TestType } from '@/types/TestReview';

const getPrimaryQuestionStatus = (question: Question): QuestionStatus => {
    if (question.isCorrect) {
        return QuestionStatus.correct;
    }

    if (!question.selected) {
        return QuestionStatus.skipped;
    }

    return QuestionStatus.incorrect;
};

const filterByQuestionStatuses = (allQuestions: Question[], statuses: string[]): Question[] => {
    if (statuses.length === 0) {
        return allQuestions;
    }

    return allQuestions.filter((question: Question) => {
        const hasFlaggedFilter = statuses.includes(QuestionStatus.flagged);
        const remainingFilters = hasFlaggedFilter
            ? statuses.filter((filter) => filter !== QuestionStatus.flagged) : statuses;

        const hasMatchingFlaggedStatus = hasFlaggedFilter ? question.marked : false;
        const hasMatchingPrimaryStatus = remainingFilters.length === 0
            ? false : remainingFilters.includes(getPrimaryQuestionStatus(question));

        return hasMatchingFlaggedStatus || hasMatchingPrimaryStatus;
    });
};

type TitleGenerator = (exam: ExamPerformanceTypes) => string;

const titleGenerators: Record<TestType, TitleGenerator> = {
    [TestType.Final]: () => 'Final Quiz',
    [TestType.Optional]: () => 'Optional Quiz',
    [TestType.Practice]: (practiceTest: ExamPerformanceTypes) => {
        const testNumber = (practiceTest as PracticePerformance).number;

        return testNumber > 2
            ? `Bonus Test #${testNumber - 2}`
            : `Practice Test #${testNumber}`;
    },
    [TestType.Stage]: (stageTest: ExamPerformanceTypes) => `Stage Test #${(stageTest as StagePerformance).stageNumber}`,
    [TestType.Eoc]: () => 'End of Course Test',
    [TestType.Study]: () => 'Study Questions',
};

const getStudyUnitNumber = (exam: ExamPerformanceTypes) => {
    if (Object.prototype.hasOwnProperty.call(exam, 'studyUnitNumber')) {
        return (exam as StudyUnitPerformance).studyUnitNumber;
    }

    return null;
};

export {
    getPrimaryQuestionStatus, filterByQuestionStatuses, titleGenerators, getStudyUnitNumber,
};
